import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { listenToUserData, updateTimeSlot, TIME_SLOTS } from '../services/firestoreService';
import TimeSlotModal from './TimeSlotModal';

const DailyView = () => {
  const { user, date } = useParams();
  const navigate = useNavigate();
  const [slotData, setSlotData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Format date for display
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Get card color based on status
  const getCardColor = (status) => {
    switch (status) {
      case 'none':
        return 'bg-white hover:bg-gray-50 border-gray-300';
      case 'done':
        return 'bg-green-600 hover:bg-green-700 border-green-700 text-white';
      case 'nothing':
        return 'bg-red-500 hover:bg-red-600 border-red-700 text-white';
      default:
        return 'bg-white hover:bg-gray-50 border-gray-300';
    }
  };

  // Check if date is in the past (before today)
  const isDateInPast = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    const checkDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Get effective status considering auto-red logic for past dates
  const getEffectiveStatus = (slot) => {
    if (isDateInPast(date) && slot.status === 'none') {
      return 'nothing'; // Auto-mark as red for past dates
    }
    return slot.status;
  };

  // Handle card click
  const handleCardClick = (timeSlot) => {
    setSelectedSlot(timeSlot);
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedSlot(null);
  };

  // Handle slot update
  const handleSlotUpdate = async (status, text = '') => {
    if (!selectedSlot) return;

    try {
      await updateTimeSlot(user, date, selectedSlot, status, text);
      // Real-time listener will update the UI automatically
      handleModalClose();
    } catch (error) {
      console.error('Error updating slot:', error);
      alert('Error updating slot. Please try again.');
    }
  };

  // Set up real-time listener
  useEffect(() => {
    const unsubscribe = listenToUserData(user, date, (data) => {
      setSlotData(data);
      setLoading(false);
    });

    return () => {
      unsubscribe(); // Clean up listener when component unmounts
    };
  }, [user, date]);

  // Convert time slot to 12-hour format
  const formatTime = (timeSlot) => {
    const [hours] = timeSlot.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour} ${period}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/calendar/${user}`)}
              className="text-gray-600 hover:text-gray-800 font-medium"
            >
              ← Back to Calendar
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-800">
                {user}'s Work Day
              </h1>
              <p className="text-gray-600 mt-1">
                {formatDate(date)}
              </p>
            </div>
            <div className="w-32"></div> {/* Spacer for centering */}
          </div>
        </div>

        {/* Time Slot Cards */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="text-gray-500">Loading work data...</div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {TIME_SLOTS.map((timeSlot) => {
              const slot = slotData[timeSlot] || { status: 'none', text: '' };
              const effectiveStatus = getEffectiveStatus(slot);
              const colorClass = getCardColor(effectiveStatus);
              
              return (
                <div
                  key={timeSlot}
                  onClick={() => handleCardClick(timeSlot)}
                  className={`p-6 rounded-xl border-2 cursor-pointer 
                             transition-all duration-200 transform hover:scale-105 
                             ${colorClass} card-transition`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold">
                      {formatTime(timeSlot)}
                    </h3>
                    <div className={`w-3 h-3 rounded-full ${
                      effectiveStatus === 'done' || effectiveStatus === 'nothing' ? 'bg-white' : 'bg-gray-400'
                    }`}></div>
                  </div>
                  
                  <div className="text-sm">
                    {effectiveStatus === 'none' ? (
                      <span className="text-gray-500">
                        Click to add work
                      </span>
                    ) : effectiveStatus === 'done' ? (
                      <div>
                        <div className="font-semibold mb-1">✓ Work Completed</div>
                        {slot.text && (
                          <div className="text-xs opacity-90 line-clamp-3">
                            {slot.text}
                          </div>
                        )}
                      </div>
                    ) : effectiveStatus === 'nothing' ? (
                      <div>
                        <div className="font-semibold mb-1">
                          {isDateInPast(date) && slot.status === 'none' ? '✗ Auto-marked (Missed)' : '✗ Did Nothing'}
                        </div>
                        <div className="text-xs opacity-90">
                          {isDateInPast(date) && slot.status === 'none' ? 'No work completed during this time' : 'No work completed during this time'}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500">
                        Click to add work
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary Stats */}
        {!loading && (
          <div className="bg-white rounded-xl shadow-md p-6 mt-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Day Summary</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-600">
                  {TIME_SLOTS.filter(slot => {
                    const effectiveStatus = getEffectiveStatus(slotData[slot] || { status: 'none', text: '' });
                    return effectiveStatus === 'none';
                  }).length}
                </div>
                <div className="text-sm text-gray-500">Empty Slots</div>
              </div>
              <div className="bg-red-100 rounded-lg p-4">
                <div className="text-2xl font-bold text-red-600">
                  {TIME_SLOTS.filter(slot => {
                    const effectiveStatus = getEffectiveStatus(slotData[slot] || { status: 'none', text: '' });
                    return effectiveStatus === 'nothing';
                  }).length}
                </div>
                <div className="text-sm text-gray-500">Did Nothing</div>
              </div>
              <div className="bg-green-100 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600">
                  {TIME_SLOTS.filter(slot => 
                    slotData[slot] && slotData[slot].status === 'done'
                  ).length}
                </div>
                <div className="text-sm text-gray-500">Completed</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal for slot editing */}
      {modalOpen && selectedSlot && (
        <TimeSlotModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          timeSlot={selectedSlot}
          currentTime={slotData[selectedSlot] || { status: 'none', text: '' }}
          onSave={handleSlotUpdate}
          formatTime={formatTime}
        />
      )}
    </div>
  );
};

export default DailyView;
